
import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { Profile } from '@/types/auth';
import { supabase } from '@/integrations/supabase/client';
import { trackAuthEvents, identifyUser, setUserProperties, syncUserIdentity } from '@/utils/mixpanel';
import { setVoiceStyle, setMuted, type VoiceStyle } from '@/lib/vovyVoice';
import { persistAttribution } from '@/lib/attribution';

export function useAuthService() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      // Always fetch fresh data from server
      const { data: userRow, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (userError) throw userError;
      if (!userRow) return; // No profile yet, skip silently

      // Fetch latest active purchase to ensure accurate subscription state
      const { data: purchases, error: purchasesError } = await supabase
        .from('purchases')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);

      if (purchasesError) {
        console.warn('Failed to load purchases:', purchasesError);
      }

      const activePurchase = purchases && purchases.length > 0 ? purchases[0] : null;

      // Build Profile object using server data only
      // Prefer users table subscription_status (set by webhook, authoritative) over purchase status
      const effectiveStatus = userRow.subscription_status ?? (activePurchase?.status as any) ?? null;
      
      // Safety net: if status is not 'trialing', never expose trial_ends_at
      // This catches discount-code users whose trial was suppressed by the webhook
      const effectiveTrialEndsAt = (effectiveStatus as string) === 'trialing' ? (userRow.trial_ends_at || null) : null;

      const profileData: Profile = {
        id: userRow.id,
        auth_user_id: userRow.auth_user_id,
        active_paying: userRow.active_paying || false,
        subscription_status: effectiveStatus,
        subscription_tier: null,
        active_subscription_tier: (activePurchase?.tier as any) ?? userRow.active_subscription_tier ?? 'web',
        email: userRow.email || (user?.email || ''),
        first_name: userRow.first_name || (user?.user_metadata?.first_name ?? ''),
        last_name: userRow.last_name || (user?.user_metadata?.last_name ?? ''),
        created_at: userRow.created_at || new Date().toISOString(),
        updated_at: '',
        stripe_customer_id: activePurchase?.stripe_customer_id || userRow.stripe_customer_id || '',
        subscription_period_end: activePurchase?.current_period_end || userRow.subscription_period_end || '',
        purchases: userRow.purchases || null,
        company_entity: userRow.company_entity || null,
        learn_lavel: userRow.learn_lavel || 'default',
        // Learning journey fields - defaults
        achievements: userRow.achievements || [],
        learning_progress: userRow.learning_progress || {},
        completed_modules: userRow.completed_modules || [],
        completed_prompts: userRow.completed_prompts || [],
        xp: userRow.xp || 0,
        level: userRow.level || 1,
        streak: userRow.streak || 0,
        streak_last_updated: userRow.streak_last_updated || null,
        total_lessons_completed: userRow.total_lessons_completed || 0,
        last_activity: userRow.last_activity || null,
        onboarding_completed: userRow.onboarding_completed ?? false,
        onboarding_completed_at: userRow.onboarding_completed_at || null,
        build_goal: userRow.build_goal || null,
        coding_experience: userRow.coding_experience || null,
        referral_source: userRow.referral_source || null,
        // Vovy voice preferences
        voice_enabled: (userRow as any).voice_enabled ?? true,
        voice_style: (userRow as any).voice_style ?? 'energetic',
        // Vovy Go fields
        go_tier: userRow.go_tier || 'free',
        go_subscription_status: userRow.go_subscription_status || null,
        go_subscription_period_end: userRow.go_subscription_period_end || null,
        go_stripe_subscription_id: userRow.go_stripe_subscription_id || null,
        extension_message_count: userRow.extension_message_count || 0,
        extension_credits_balance: userRow.extension_credits_balance || 0,
        extension_usage_month: userRow.extension_usage_month || null,
        extension_monthly_limit: userRow.extension_monthly_limit || null,
        trial_ends_at: effectiveTrialEndsAt,
      };

      setProfile(profileData);

      // Keep Mixpanel user attributes in lockstep with the profile row —
      // the users table is authoritative for name/email (SSO metadata can
      // be missing) and for paying status (set by the Stripe webhook).
      const fullName = [profileData.first_name, profileData.last_name].filter(Boolean).join(' ').trim();
      setUserProperties({
        ...(profileData.email ? { $email: profileData.email } : {}),
        ...(fullName ? { $name: fullName } : {}),
      });

      // Hydrate the runtime voice from the saved prefs so Vovy speaks in the
      // user's chosen voice (and stays muted if they turned voice off).
      try {
        setVoiceStyle((profileData.voice_style as VoiceStyle) || 'energetic');
        setMuted(!(profileData.voice_enabled ?? true));
      } catch {}
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Watchdog: never leave the app stuck on a loading spinner if the profile
    // fetch hangs (e.g. backend unreachable / Supabase project paused). The
    // queries in fetchProfile have no built-in timeout, so guarantee `loading`
    // resolves so guarded pages can render.
    const loadingWatchdog = setTimeout(() => {
      console.warn('⏱️ Auth loading watchdog fired — forcing loading=false (profile fetch may have stalled)');
      setLoading(false);
    }, 8000);

    // Set up auth state listener FIRST for reliable session handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔄 Auth state change:', event, !!session?.user);
      // Only synchronous state updates in the callback
      setUser(session?.user ?? null);

      if (session?.user) {
        // Identity → Mixpanel on every session start (retries internally
        // until the deferred mixpanel init lands). This is the chokepoint
        // that guarantees $email/$name for SSO users, whose signup forms
        // never pass through trackAuthEvents.
        syncUserIdentity(session.user);
        // Defer any Supabase calls to avoid deadlocks
        setTimeout(() => {
          fetchProfile(session.user.id);
          // Ad attribution → users.attribution. Same reasoning as the identity
          // sync above: this listener is the one path every authenticated
          // session goes through (email signup, OAuth popup, OAuth redirect,
          // restore), and the signup forms don't persist it themselves.
          // First-touch wins, no-ops once settled, never throws.
          persistAttribution(session.user.id);
        }, 0);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    // THEN check for existing session
    (async () => {
      try {
        console.log('🔄 Initializing auth state...');
        const { data: { session } } = await supabase.auth.getSession();
        console.log('✅ Initial session retrieved:', !!session?.user);
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        setLoading(false);
      }
    })();

    return () => {
      clearTimeout(loadingWatchdog);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signInWithEmail = async (email: string, password: string) => {
    trackAuthEvents.signInStarted('email');
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    if (data.user) {
      trackAuthEvents.signInCompleted(data.user.id, email);
    }
  };

  const signInWithGoogle = async () => {
    trackAuthEvents.signInStarted('google');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      }
    });
    if (error) throw error;
  };

  const signInWithGitHub = async () => {
    trackAuthEvents.signInStarted('github');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      }
    });
    if (error) throw error;
  };

  // Apple OAuth (parity with the mobile app). Requires the Apple provider to be
  // enabled in the Supabase dashboard (Services ID + key); the web flow itself
  // is the standard OAuth redirect.
  const signInWithApple = async () => {
    trackAuthEvents.signInStarted('apple' as any);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      }
    });
    if (error) throw error;
  };

  const signUpWithEmail = async (email: string, password: string, userData?: any) => {
    trackAuthEvents.signUpStarted('email');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    if (data.user) {
      trackAuthEvents.signUpCompleted(data.user.id, email, 'email');
      // Loops contact sync + welcome email run server-side via the
      // Loops ↔ Supabase auth.users webhook and the public.users DB webhook.
    }
  };

  const signOut = async () => {
    console.log('🚪 Signing out user...');
    trackAuthEvents.signOut();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setProfile(null);
    console.log('✅ User signed out successfully');
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return {
    user,
    profile,
    loading,
    signIn,
    signInWithEmail,
    signInWithGoogle,
    signInWithGitHub,
    signInWithApple,
    signUpWithEmail,
    signOut,
    refreshProfile,
    supabase
  };
}
